import { PatientLayout } from "@/components/layout/patient-layout";
import { useGetMedication, getGetMedicationQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pill, AlertCircle, ClipboardCheck, BookOpen, Info, CheckCircle, ShieldAlert } from "lucide-react";
import { useEffect } from "react";
import { recordRecentView } from "@/hooks/use-recently-viewed";
import { BackButton } from "@/components/ui/back-button";

type DosageEntry = { route: string; dose: string; frequency: string; notes?: string };

export default function PatientKnowYourMedicationDetail() {
  const { id } = useParams();
  const { data: medication, isLoading, error } = useGetMedication(Number(id), {
    query: { enabled: !!id, queryKey: getGetMedicationQueryKey(Number(id)) },
  });

  useEffect(() => {
    if (!medication) return;
    recordRecentView({
      type: "medication",
      id: medication.id,
      name: medication.name,
      href: `/patient/know-your-medication/${medication.id}`,
    });
  }, [medication?.id]);

  if (isLoading) return <PatientLayout title="Know Your Medication"><LoadingScreen /></PatientLayout>;
  if (error || !medication) return (
    <PatientLayout title="Know Your Medication">
      <div className="p-8 text-slate-500">Medication information not found.</div>
    </PatientLayout>
  );

  const dosageInfo = (medication.dosageInfo ?? []) as DosageEntry[];
  const hasSideEffects = medication.sideEffects && medication.sideEffects.length > 0;
  const hasContraindications = medication.contraindications && medication.contraindications.length > 0;

  return (
    <PatientLayout title={medication.name}>
      <BackButton href="/patient/know-your-medication">All Medicines</BackButton>

      {/* Disclaimer — prominent at top */}
      <div className="mb-8">
        <UkHealthDisclaimer />
      </div>

      {/* Hero */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-8 sm:p-12 border-b border-indigo-100 flex items-center gap-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm text-indigo-500 hidden sm:block shrink-0">
            <Pill className="h-10 w-10" />
          </div>
          <div>
            {medication.drugClass && (
              <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-1">{medication.drugClass}</p>
            )}
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{medication.name}</h1>
            {medication.genericName && medication.genericName !== medication.name && (
              <p className="text-base text-slate-500">Also known as: {medication.genericName}</p>
            )}
          </div>
        </div>

        {(medication.patientSummary || medication.patientNotes) && (
          <div className="p-8 sm:p-12">
            <div className="flex items-start gap-4">
              <div className="bg-indigo-100 p-3 rounded-2xl shrink-0">
                <Info className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">What is it for?</h2>
                <p className="text-slate-700 leading-relaxed text-lg">
                  {medication.patientSummary ?? medication.patientNotes}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* How to Take It */}
      {medication.patientMedicationGuide && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-teal-100 p-2.5 rounded-2xl">
              <ClipboardCheck className="h-6 w-6 text-teal-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">How to Take It</h2>
          </div>
          <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <p className="text-teal-900 leading-relaxed whitespace-pre-wrap">{medication.patientMedicationGuide}</p>
          </div>
        </div>
      )}

      {/* Dosage */}
      {dosageInfo.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2.5 rounded-2xl">
              <BookOpen className="h-6 w-6 text-blue-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Dosage Information</h2>
          </div>
          <p className="text-slate-500 mb-5 text-sm">Your doctor will give you your exact dose. This is for reference only.</p>
          <div className="space-y-3">
            {dosageInfo.map((d, i) => (
              <div key={i} className="border border-slate-200 rounded-2xl p-5">
                <div className="flex flex-wrap gap-6 mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">How it is given</p>
                    <p className="font-medium text-slate-800">{d.route}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Typical dose</p>
                    <p className="font-medium text-slate-800">{d.dose}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">How often</p>
                    <p className="font-medium text-slate-800">{d.frequency}</p>
                  </div>
                </div>
                {d.notes && (
                  <p className="text-sm text-slate-600 bg-amber-50 rounded-xl px-4 py-2 border border-amber-100">{d.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side Effects */}
      {hasSideEffects && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-2.5 rounded-2xl">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Possible Side Effects</h2>
          </div>
          <p className="text-slate-500 mb-5 text-sm">Not everyone gets these. Contact your team if any side effects are severe or worrying.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {medication.sideEffects!.map((s, i) => (
              <div key={i} className="flex items-start gap-2 bg-amber-50 rounded-xl p-3 border border-amber-100 text-sm text-amber-900">
                <span className="text-amber-500 shrink-0 mt-0.5">•</span>
                {s}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* When NOT to take */}
      {hasContraindications && (
        <div className="bg-rose-50 rounded-3xl border border-rose-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <ShieldAlert className="h-6 w-6 text-rose-600 shrink-0" />
            <h3 className="text-xl font-bold text-rose-900">Important Warnings</h3>
          </div>
          <p className="text-rose-700 mb-4 text-sm">Tell your doctor or pharmacist if any of these apply to you:</p>
          <ul className="space-y-2">
            {medication.contraindications!.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-rose-800 text-sm">
                <span className="text-rose-500 shrink-0 mt-0.5">✗</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Blood Tests & Monitoring */}
      {medication.monitoringRequirements && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-green-100 p-2.5 rounded-2xl">
              <CheckCircle className="h-6 w-6 text-green-700" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Blood Tests and Monitoring</h3>
          </div>
          <p className="text-slate-700 leading-relaxed text-sm">{medication.monitoringRequirements}</p>
        </div>
      )}

      {/* Disclaimer at bottom */}
      <UkHealthDisclaimer />
    </PatientLayout>
  );
}
